import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Input,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { submitWebsiteLead } from '@/api/leads';
import { useMyContact } from '@/api/contact';
import { useAuth } from '@/auth/AuthProvider';
import { brand } from '@/config/brand';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ContactForm {
  name: string;
  workEmail: string;
  phone: string;
  comments: string;
}

const EMPTY: ContactForm = { name: '', workEmail: '', phone: '', comments: '' };

interface ServiceContactSalesDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Product name — shown in the copy and tagged on the sales lead. */
  serviceLabel: string;
  /**
   * Canonical DataCentral product-group for the lead's interested service (the
   * CRM "Giải pháp" field). Falls back to `serviceLabel` when omitted, but that
   * won't match a product-group option — always pass it for the heavy services.
   */
  productGroup?: string;
  /** Optional dialog title override (defaults to "Talk to an expert"). */
  title?: string;
  /** Optional description override. */
  description?: string;
}

/**
 * "Talk to an expert" — a shared contact-sales dialog for the heavy cloud
 * services (Compute, Kubernetes, Object Storage, Multi-CDN). Instead of
 * self-serve provisioning, it submits a sales lead to DataCentral
 * (/cms/submit, via submitWebsiteLead) so a rep can scope and activate the
 * service; mirrors the WAAP Custom plan's PlanContactDialog.
 */
export function ServiceContactSalesDialog({
  open,
  onOpenChange,
  serviceLabel,
  productGroup,
  title,
  description,
}: ServiceContactSalesDialogProps) {
  const { show } = useToast();
  const { user } = useAuth();
  const { data: contact, isFetched } = useMyContact();
  const [f, setF] = useState<ContactForm>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const set = (patch: Partial<ContactForm>) => setF((prev) => ({ ...prev, ...patch }));

  // Seed the name only from a real name on file — never derive it from the email.
  // Some contact records store the email's local-part (the bit before "@") as the
  // name; treat that as no-name and leave the field blank so the user types their
  // own, rather than dropping an email-shaped placeholder into the name field.
  const seedEmail = contact?.email || user?.email || '';
  const emailLocal = seedEmail.split('@')[0]?.trim().toLowerCase() ?? '';
  const rawName = (contact?.name || user?.name || '').trim();
  const seedName =
    rawName && !rawName.includes('@') && rawName.toLowerCase() !== emailLocal ? rawName : '';
  const seedPhone = contact?.phone ?? '';

  // Pre-fill from the signed-in partner's DataCentral contact (falling back to
  // the account token). Seed once the contact query has settled so phone is
  // included when available. Email is authoritative (kept read-only).
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || (!isFetched && !user?.email)) return;
    seededRef.current = true;
    setF((prev) => ({ ...prev, name: seedName, workEmail: seedEmail, phone: seedPhone }));
  }, [isFetched, seedName, seedEmail, seedPhone, user?.email]);

  const canSubmit = Boolean(
    f.name.trim() && EMAIL_RE.test(f.workEmail.trim()) && f.phone.trim() && !submitting,
  );

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await submitWebsiteLead({
        firstname: f.name.trim(),
        email: f.workEmail.trim(),
        phone: f.phone.trim(),
        interestedSolutions: [productGroup ?? serviceLabel],
        specificNeeds: f.comments.trim() || undefined,
      });
      show({ tone: 'success', title: 'Request sent', description: 'Our team will contact you shortly.' });
      // Keep the pre-filled identity so a re-open still shows the account info.
      setF({ ...EMPTY, name: seedName, workEmail: seedEmail, phone: seedPhone });
      onOpenChange(false);
    } catch {
      show({ tone: 'danger', title: 'Could not send your request', description: 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title ?? 'Talk to an expert'}</DialogTitle>
          <DialogDescription>
            {description ??
              `Tell us about your needs and a specialist will help set up ${serviceLabel} for your organization.`}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Name" required>
              {(p) => <Input {...p} value={f.name} onChange={(e) => set({ name: e.target.value })} />}
            </Field>
            <Field label="Work email" required description={`From your ${brand.shortName} account.`}>
              {(p) => <Input {...p} type="email" value={f.workEmail} disabled />}
            </Field>
            <Field label="Phone" required>
              {(p) => <Input {...p} type="tel" value={f.phone} onChange={(e) => set({ phone: e.target.value })} />}
            </Field>
            <Field label="Comments">
              {(p) => (
                <Textarea
                  {...p}
                  rows={3}
                  value={f.comments}
                  onChange={(e) => set({ comments: e.target.value })}
                />
              )}
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            {submitting ? 'Sending…' : 'Talk to an expert'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ServiceContactSalesDialog;
